import React, { useState, useEffect, useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import type {
  ProfileResponse,
  SspInheritance,
} from "../../../../../api/frameworksApi";
import {
  FormField,
  FormInput,
  FormSelect,
  Modal,
  ModalFooterButtons,
} from "../../../../components/Modal";
import {
  SSP_INHERITANCE_TYPE_OPTIONS,
  type SspInheritanceType,
} from "../../constants";
import {
  sanitizeSspShortCode,
  SSP_SHORT_CODE_MAX,
} from "../../../../../utils/formatters";
// Same brand water-fill loader the report preview uses, so every modal that
// waits on data looks the same.
import { ReportWaterLoader } from "../../../Report2/audit-report/ReportWaterLoader";

import type { AccessibleOrg, CreateSspPayload } from "../../types";
export type { AccessibleOrg, CreateSspPayload };

/** Shape of the create-SSP form, kept local while the dialog is open. */
export interface SspFormState {
  title: string;
  shortCode: string;
  importProfileHref: string;
  owningOrgUnitId: string;
}

/** Stable empty list for the loading-state selects — an inline `[]` would be
 *  a new array on every render and defeat the memo inside FormSelect. */
const NO_OPTIONS: { value: string; label: string }[] = [];

const EMPTY_SSP_FORM: SspFormState = {
  title: "",
  shortCode: "",
  importProfileHref: "",
  owningOrgUnitId: "",
};

export interface NewSspDialogProps {
  open: boolean;
  onClose: () => void;
  sspForm: SspFormState;
  setSspForm: React.Dispatch<React.SetStateAction<SspFormState>>;
  sspInheritanceType: SspInheritanceType;
  setSspInheritanceType: React.Dispatch<React.SetStateAction<SspInheritanceType>>;
  sspSelectedAppId: string;
  setSspSelectedAppId: React.Dispatch<React.SetStateAction<string>>;
  sspSelectedServiceIds: string[];
  setSspSelectedServiceIds: React.Dispatch<React.SetStateAction<string[]>>;
  setInheritedIds: React.Dispatch<React.SetStateAction<string[]>>;
  creating: boolean;
  loadingProfiles: boolean;
  profiles: ProfileResponse[];
  accessibleOrgs: AccessibleOrg[];
  accessibleOrgsLoading: boolean;
  sspInheritanceLoading: boolean;
  sspInheritance: SspInheritance;
  sspApplicationOptions: { value: string; label: string }[];
  sspServiceOptions: { value: string; label: string }[];
  sspTypeSelectionIncomplete: boolean;
  setShowInheritancePicker: React.Dispatch<React.SetStateAction<boolean>>;
  handleCreateSSP: (
    payload?: CreateSspPayload,
    formOverride?: SspFormState,
  ) => Promise<void> | void;
}

export function NewSspDialog({
  open,
  onClose,
  sspForm,
  setSspForm,
  sspInheritanceType,
  setSspInheritanceType,
  sspSelectedAppId,
  setSspSelectedAppId,
  sspSelectedServiceIds,
  setSspSelectedServiceIds,
  setInheritedIds,
  creating,
  loadingProfiles,
  profiles,
  accessibleOrgs,
  accessibleOrgsLoading,
  sspInheritanceLoading,
  sspInheritance,
  sspApplicationOptions,
  sspServiceOptions,
  sspTypeSelectionIncomplete,
  setShowInheritancePicker,
  handleCreateSSP,
}: NewSspDialogProps) {
  // Show the short-code required error only after the user has interacted.
  const [shortCodeTouched, setShortCodeTouched] = useState(false);
  const [form, setForm] = useState<SspFormState>(sspForm);
  useEffect(() => {
    if (open) {
      setForm(sspForm);
      setShortCodeTouched(false);
    }
  }, [open]);
  // The dialog opens while profiles and organizations are still in flight.
  // Both feed dropdowns on the first screen, so the whole form waits on them
  // rather than rendering fields the user can't fill in yet. Applications /
  // services aren't included — they only start loading once a Type is picked
  // and keep their own per-field loading state.
  const initialLoading = loadingProfiles || accessibleOrgsLoading;

  // Typing in SSP Name / Short Code re-renders this dialog on every
  // keystroke. The organization list can be thousands of entries long
  // (the accessible-orgs endpoint reports totalAccessible in the
  // thousands), so building these option arrays inline made each
  // character cost a full rebuild of the list — that is what made the
  // two text inputs feel laggy compared to the rest of the form.
  const profileOptions = useMemo(
    () =>
      profiles
        .filter((p) => p.lifecycleStatus === "published")
        .map((p) => ({ value: `#${p.documentId}`, label: p.title })),
    [profiles],
  );
  const orgOptions = useMemo(
    () =>
      accessibleOrgs.map((o) => ({
        value: o.id,
        // Name only: the short code is an internal identifier and the org id
        // is what the backend is given.
        label: o.name,
      })),
    [accessibleOrgs],
  );

  const resetAllOnClose = () => {
    onClose();
    setForm(EMPTY_SSP_FORM);
    setSspForm(EMPTY_SSP_FORM);
    setShortCodeTouched(false);
    setSspInheritanceType("");
    setSspSelectedAppId("");
    setSspSelectedServiceIds([]);
    setInheritedIds([]);
  };
  return (
    <Modal
      open={open}
      onClose={resetAllOnClose}
      title="New System Security Plan"
      description="Create a new OSCAL-native SSP."
      size="md"
      footer={
        <ModalFooterButtons
          onCancel={resetAllOnClose}
          onSubmit={async () => {
            setSspForm(form);
            if (sspInheritanceType === "service" && sspSelectedServiceIds.length) {
              // Union the components of every selected service, de-duped —
              // a component can be shared across services.
              const componentIds = Array.from(
                new Set(
                  sspInheritance.applications
                    .flatMap((a) => a.services ?? [])
                    .filter((s) => sspSelectedServiceIds.includes(s.serviceId))
                    .flatMap(
                      (s) => s.components?.map((c) => c.componentId) ?? [],
                    ),
                ),
              );
              onClose();
              await handleCreateSSP(
                { serviceIds: sspSelectedServiceIds, componentIds },
                form,
              );
              return;
            }
            if (sspInheritanceType === "application" && sspSelectedAppId) {
              const app = sspInheritance.applications.find(
                (a) => a.applicationId === sspSelectedAppId,
              );
              const hasServices = (app?.services?.length ?? 0) > 0;
              if (!hasServices) {
                const directComponentIds =
                  app?.components?.map((c) => c.componentId) ?? [];
                onClose();
                await handleCreateSSP(
                  {
                    applicationId: sspSelectedAppId,
                    componentIds: directComponentIds,
                  },
                  form,
                );
                return;
              }
            }
            // Otherwise → keep the picker step so the user can choose
            // which services/components to inherit.
            onClose();
            setShowInheritancePicker(true);
          }}
          submitLabel={(() => {
            if (initialLoading) return "Loading";
            if (creating) return "Creating SSP";
            if (sspInheritanceType === "service") return "Create SSP";
            if (sspInheritanceType === "application" && sspSelectedAppId) {
              const app = sspInheritance.applications.find(
                (a) => a.applicationId === sspSelectedAppId,
              );
              const hasServices = (app?.services?.length ?? 0) > 0;
              if (!hasServices) return "Create SSP";
            }
            return "Next";
          })()}
          submitDisabled={
            initialLoading ||
            !form.title ||
            !form.shortCode.trim() ||
            !form.importProfileHref ||
            sspTypeSelectionIncomplete ||
            (accessibleOrgs.length > 1 && !form.owningOrgUnitId) ||
            creating
          }
        />
      }
    >
      {initialLoading && (
        // min-h keeps the modal roughly form-sized so it doesn't resize
        // when the loader is swapped out for the fields.
        <div className="flex min-h-[320px] items-center justify-center">
          <ReportWaterLoader label="Loading" />
        </div>
      )}
      {/* Kept mounted (just hidden) while loading so local form state and
          focus survive the switch. space-y-4 mirrors the modal body's own
          spacing, which only applies to its direct children. */}
      <div className={initialLoading ? "hidden" : "space-y-4"}>
      <FormField label="SSP Name" required>
        <FormInput
          placeholder="e.g. Production Cloud Platform SSP"
          value={form.title}
          onChange={(v) => setForm((f) => ({ ...f, title: v }))}
        />
      </FormField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField
          label="Profile"
          required
          hint="Select the profile baseline for this SSP"
        >
          <FormSelect
            options={profileOptions}
            placeholder={loadingProfiles ? "Loading profiles" : "Select a profile"}
            searchable
            loading={loadingProfiles}
            value={form.importProfileHref}
            onChange={(v) => setForm((f) => ({ ...f, importProfileHref: v }))}
          />
        </FormField>
        <FormField label="SSP Short Code" required>
          <FormInput
            placeholder="e.g. CMBA"
            value={form.shortCode}
            maxLength={SSP_SHORT_CODE_MAX}
            onChange={(v) => {
              // Normalize to what the backend stores (uppercase, alphanumeric,
              // capped) — see sanitizeSspShortCode for the edge cases handled.
              setForm((f) => ({ ...f, shortCode: sanitizeSspShortCode(v) }));
              setShortCodeTouched(true);
            }}
          />
          {shortCodeTouched && !form.shortCode.trim() && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              SSP short code is required
            </p>
          )}
        </FormField>
        <FormField label="Owning Unit" required>
          {accessibleOrgsLoading ? (
            <FormSelect
              options={NO_OPTIONS}
              placeholder="Loading organizations"
              loading
              value=""
            />
          ) : accessibleOrgs.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
              <AlertTriangle className="size-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-600 dark:text-red-400">
                No accessible organizations.
              </span>
            </div>
          ) : accessibleOrgs.length === 1 ? (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
              <span className="font-medium">{accessibleOrgs[0].name}</span>
            </div>
          ) : (
            <FormSelect
              options={orgOptions}
              value={form.owningOrgUnitId}
              onChange={(v) => setForm((f) => ({ ...f, owningOrgUnitId: v }))}
              placeholder="Select group & organization"
              searchable
            />
          )}
        </FormField>
        <FormField
          label="Type"
          required
          hint="Select SSP implementation type"
        >
          <FormSelect
            options={SSP_INHERITANCE_TYPE_OPTIONS}
            placeholder="Select type"
            searchable
            value={sspInheritanceType}
            onChange={(v) => {
              setSspInheritanceType(v as SspInheritanceType);
              setSspSelectedAppId("");
              setSspSelectedServiceIds([]);
            }}
          />
        </FormField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sspInheritanceType === "application" && (
          <FormField
            label="Application"
            required
            hint="Choose linked application"
          >
            <FormSelect
              options={sspApplicationOptions}
              placeholder={
                sspInheritanceLoading
                  ? "Loading applications"
                  : "Select an application"
              }
              searchable
              loading={sspInheritanceLoading}
              value={sspSelectedAppId}
              onChange={setSspSelectedAppId}
            />
          </FormField>
        )}
        {sspInheritanceType === "service" && (
          <FormField label="Service" required hint="Choose linked service">
            <FormSelect
              options={sspServiceOptions}
              placeholder={
                sspInheritanceLoading ? "Loading services" : "Select a service"
              }
              searchable
              loading={sspInheritanceLoading}
              value={sspSelectedServiceIds[0] ?? ""}
              // State stays an array to keep the existing plumbing; the
              // create call sends only the first entry as service_id.
              onChange={(v) => setSspSelectedServiceIds(v ? [v] : [])}
            />
          </FormField>
        )}
      </div>
      </div>
    </Modal>
  );
}
